import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { commafy } from '../../../utils/methods';
import { _post } from '../../../utils/network';
import { contentLevelLabel, fetchBlogCategoryOptions, toShamsiDate } from '../blogUtils';

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb.component';
import FormField from '../../../components/Form/FormField/FormField.component';
import LoadingButton from '../../../components/LoadingButton/LoadingButton.component';
import ListTable from '../../../components/List/ListTable/ListTable.component';
import Pagination from '../../../components/List/Pagination/Pagination.component';

const optionToBoolean = (value, trueValue, falseValue) => value === trueValue ? true : value === falseValue ? false : null;

const BlogPostsList = () => {
	const { t } = useTranslation();
	const [blogPosts, setBlogPosts] = useState([]);
	const [blogCategories, setBlogCategories] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	const [searchButtonIsLoading, setSearchButtonIsLoading] = useState(false);
	const [searchParams, setSearchParams] = useState({
		title: null,
		category_id: null,
		is_special: null,
		is_active: null,
		sort: null,
		page: 1,
		count: 10
	});

	const breadcrumbItems = [
		{ title: t('dashboard'), link: '/dashboard' },
		{ title: t('blog_posts'), link: '/blog-posts-list' },
		{ title: t('list') }
	];
	const typesList = [
		{ key: t('normal'), value: 'normal' },
		{ key: t('special'), value: 'special' }
	];
	const activationsList = [
		{ key: t('active'), value: 'active' },
		{ key: t('deactive'), value: 'deactive' }
	];
	const sortsList = [
		{ key: t('newest'), value: 'date-desc' },
		{ key: t('oldest'), value: 'date-asc' },
		{ key: t('most_viewed'), value: 'view-desc' },
		{ key: t('least_viewed'), value: 'view-asc' }
	];

	const blogPostsListData = useMemo(() => ({
		head: [t('publishes_at'), t('title'), t('category'), t('level'), t('views'), t('type'), t('status'), t('operation')],
		body: blogPosts.map(blogPost => [
			toShamsiDate(blogPost.publishes_at),
			blogPost.title,
			blogPost.category_info?.title,
			contentLevelLabel(blogPost.level, t),
			commafy(blogPost.views_count ?? 0),
			<span className={`badge rounded-pill p-2 badge-subtle-${blogPost.is_special ? 'warning' : 'secondary'}`}>
				{blogPost.is_special ? t('special') : t('normal')}
			</span>,
			<span className={`badge rounded-pill p-2 badge-subtle-${blogPost.is_active ? 'success' : 'secondary'}`}>
				{blogPost.is_active ? t('active') : t('deactive')}
			</span>,
			<Link className='btn btn-secondary btn-sm p-1' to={`/blog-posts-edit/${blogPost.id}`}>
				<i className="fa fa-edit"></i> {t('edit')}
			</Link>
		]),
		directions: ['ltr', '', '', '', 'ltr', '', '', 'ltr']
	}), [blogPosts, t]);

	const getBlogCategories = async () => {
		const { options, response } = await fetchBlogCategoryOptions();
		if(response.code !== 200)
			return toast.error(response[t('api_message')]);
		setBlogCategories(options);
	};

	const getBlogPosts = async () => {
		setSearchButtonIsLoading(true);
		const response = await _post('/blog_posts/admin/getBlogPosts', searchParams);
		setSearchButtonIsLoading(false);
		switch(response.code){
			case 200:
				setTotalCount(response.data.blog_posts_count);
				setBlogPosts(response.data.blog_posts);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};

	const filterBlogPosts = event => {
		event.preventDefault();
		if(searchButtonIsLoading)
			return;
		const formData = new FormData(event.target);
		setSearchParams({
			...searchParams,
			title: formData.get('searchBlogPostTitle')?.trim() || null,
			category_id: formData.get('searchBlogPostCategory') || null,
			is_special: optionToBoolean(formData.get('searchBlogPostIsSpecial'), 'special', 'normal'),
			is_active: optionToBoolean(formData.get('searchBlogPostIsActive'), 'active', 'deactive'),
			sort: formData.get('searchBlogPostSort') || null,
			page: 1
		});
	};

	useEffect(() => {
		getBlogCategories();
	}, []);
	useEffect(() => {
		getBlogPosts();
	}, [searchParams]);

	return (
		<>
			<Breadcrumb items={breadcrumbItems} />
			<div className="card mb-3">
				<div className="card-header">
					<h5 className="mb-0">{t('filter')} {t('blog_posts')}</h5>
				</div>
				<div className="card-body bg-light">
					<form onSubmit={filterBlogPosts}>
						<div className="row gx-2">
							<FormField fieldKey="searchBlogPostTitle" title={t('title')} isSearch />
							<FormField type="select" fieldKey="searchBlogPostCategory" title={t('category')} selectItems={blogCategories} isSearch />
							<FormField type="select" fieldKey="searchBlogPostIsSpecial" title={t('type')} selectItems={typesList} isSearch />
							<FormField type="select" fieldKey="searchBlogPostIsActive" title={t('status')} selectItems={activationsList} isSearch />
							<FormField type="select" fieldKey="searchBlogPostSort" title={t('sorting')} selectItems={sortsList} isSearch required />
						</div>
						<div>
							<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="search" isLoading={searchButtonIsLoading}>
								<i className="fa fa-search"></i> {t('search')}
							</LoadingButton>
						</div>
					</form>
				</div>
			</div>
			<ListTable title={t('blog_posts')} pageNumber={searchParams.page} pageCounter={searchParams.count} totalCount={totalCount} data={blogPostsListData} />
			<Pagination pagesCount={Math.ceil(totalCount / searchParams.count)} action={page => setSearchParams({ ...searchParams, page })} />
		</>
	);
};

export default BlogPostsList;
